/**
 * ListeningPlayer — IELTS Listening practice player with paper-style
 * on-screen answer sheet.
 *
 * UX flow:
 *   PRE-AUDIO   → student can print the sheet OR type directly on screen
 *   PLAYING     → audio rolls, 30-min timer counts down, sheet locked-in
 *   REVIEW      → after submit, each line/circle shows ✓ / ✗ + correct answer
 *
 * The paper-style sheet renders text answers as live lines (transparent
 * input with bottom border) and MCQ questions as clickable A-G circles.
 *
 * Route: /app/listening-catalogue/:id
 */
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  Play, Pause, RotateCcw, FileText, ExternalLink, Send, Loader2, ChevronLeft,
  CheckCircle2, XCircle, Clock, Printer, AlertCircle,
} from "lucide-react";

export default function ListeningPlayer() {
  const { id } = useParams();
  const nav = useNavigate();
  const audioRef = useRef(null);
  const { user } = useAuth();

  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState(Array(40).fill(""));
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [stage, setStage] = useState("pre");   // pre | playing | review
  const [paused, setPaused] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30 * 60);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/listening-catalogue/${id}`);
        setTest(r.data);
      } catch {
        toast.error("Test not found");
        nav("/app/listening-catalogue");
      } finally { setLoading(false); }
    })();
  }, [id, nav]);

  // 30-min countdown
  useEffect(() => {
    if (stage !== "playing" || timeLeft <= 0) return;
    const t = setInterval(() => setTimeLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [stage, timeLeft]);

  // Auto-submit at 0
  useEffect(() => {
    if (timeLeft === 0 && stage === "playing" && !result && !submitting) {
      toast.message("Time's up — submitting your answers");
      submit();
    }
    // eslint-disable-next-line
  }, [timeLeft]);

  const startAudio = () => {
    if (!audioRef.current) return;
    audioRef.current.play().then(() => {
      setStage("playing");
    }).catch(() => {
      toast.error("Could not start audio — check your browser audio permissions.");
    });
  };

  const togglePause = () => {
    if (!audioRef.current) return;
    if (paused) { audioRef.current.play(); setPaused(false); }
    else { audioRef.current.pause(); setPaused(true); }
  };

  const fmt = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const setAnswer = (i, v) => {
    setAnswers((arr) => { const x = [...arr]; x[i] = v; return x; });
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const r = await api.post("/listening-catalogue/submit", {
        test_number: Number(id),
        answers,
      });
      setResult(r.data);
      setStage("review");
      if (audioRef.current) audioRef.current.pause();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not grade your answers");
    } finally { setSubmitting(false); }
  };

  const onPrint = () => {
    window.print();
  };

  const reset = () => {
    setResult(null);
    setStage("pre");
    setPaused(false);
    setTimeLeft(30 * 60);
    setAnswers(Array(40).fill(""));
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin" /></div>;
  }
  if (!test) return null;

  const today = new Date().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  const candidateName = user?.name || user?.email?.split("@")[0] || "";

  return (
    <div className="min-h-screen pb-16 listening-screen" data-testid="listening-player">
      <PlayerCSS />

      {/* Sticky top exam ribbon */}
      <div className="sticky top-0 z-40 backdrop-blur-md shadow-sm no-print"
           style={{ background: "rgba(255,255,255,0.92)", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
          <Link to="/app/listening-catalogue" className="inline-flex items-center gap-1 text-xs opacity-70 hover:opacity-100" data-testid="back-to-catalogue">
            <ChevronLeft className="w-4 h-4" /> Back
          </Link>
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase opacity-60">
            STEP {stage === "pre" ? "2" : stage === "playing" ? "3" : "4"} —
          </div>
          <div className="text-sm opacity-80 flex-1 min-w-[200px]">
            {stage === "pre" && "Print your answer sheet (optional) or type directly on screen."}
            {stage === "playing" && "Audio is playing — listen and write your answers."}
            {stage === "review" && "Test submitted — review your answers below."}
          </div>

          {stage !== "review" && (
            <button onClick={onPrint} data-testid="print-sheet"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-sm font-mono text-xs tracking-[0.25em] uppercase"
                    style={{ background: "#003049", color: "#fff" }}>
              <Printer className="w-4 h-4" /> Print
            </button>
          )}

          {stage === "pre" && (
            <button onClick={startAudio} data-testid="start-audio"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-sm font-mono text-xs tracking-[0.25em] uppercase animate-pulse-soft"
                    style={{ background: "#F77F00", color: "#000", boxShadow: "0 0 18px rgba(247,127,0,0.65)" }}>
              <Play className="w-4 h-4" /> Start audio
            </button>
          )}

          {stage === "playing" && (
            <div className="flex items-center gap-2">
              <button onClick={togglePause} data-testid="audio-toggle"
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm font-mono text-xs uppercase tracking-[0.2em]"
                      style={{ background: "rgba(0,0,0,0.06)" }}>
                {paused ? <><Play className="w-3.5 h-3.5" /> Resume</> : <><Pause className="w-3.5 h-3.5" /> Pause</>}
              </button>
              <div className="flex items-center gap-1.5 px-3 py-2 rounded-sm font-mono text-sm"
                   style={{ background: timeLeft < 60 ? "rgba(214,40,40,0.12)" : "rgba(247,127,0,0.10)",
                            color: timeLeft < 60 ? "#D62828" : "#B45309" }}
                   data-testid="listening-timer">
                <Clock className="w-3.5 h-3.5" /> {fmt(timeLeft)}
              </div>
              <button onClick={submit} disabled={submitting} data-testid="submit-listening"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-sm font-mono text-xs uppercase tracking-[0.25em] disabled:opacity-60"
                      style={{ background: "#F77F00", color: "#000" }}>
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Submit
              </button>
            </div>
          )}

          {stage === "review" && (
            <button onClick={reset} data-testid="retake-test"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-sm font-mono text-xs uppercase tracking-[0.25em] border"
                    style={{ borderColor: "rgba(0,0,0,0.2)" }}>
              <RotateCcw className="w-4 h-4" /> Re-take
            </button>
          )}
        </div>

        {/* Audio is invisible but mounted; controls are in the ribbon. */}
        <audio ref={audioRef} src={test.external_mp3_url}
               onPause={() => setPaused(true)} onPlay={() => setPaused(false)}
               data-testid="listening-audio" />
      </div>

      {/* PDF helper banner */}
      {stage === "pre" && (
        <div className="max-w-5xl mx-auto px-4 mt-4 no-print">
          <div className="rounded-md p-4 flex items-start gap-3"
               style={{ background: "rgba(247,127,0,0.07)", border: "1px solid rgba(247,127,0,0.3)" }}>
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "#B45309" }} />
            <div className="text-sm flex-1">
              <div className="font-semibold mb-0.5">Audio plays ONCE only — no replay.</div>
              <div className="opacity-80">
                Open the question booklet in a separate tab and have it ready before you press Start audio.
                You can write on the on-screen sheet below or print it — both work and we grade the on-screen one.
              </div>
            </div>
            <a href={test.external_pdf_url} target="_blank" rel="noreferrer" data-testid="open-pdf"
               className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm font-mono text-xs uppercase tracking-[0.2em] flex-shrink-0"
               style={{ background: "rgba(0,48,73,0.08)" }}>
              <FileText className="w-3.5 h-3.5" /> Open questions PDF <ExternalLink className="w-3 h-3 opacity-60" />
            </a>
          </div>
        </div>
      )}

      {/* Result banner */}
      {result && (
        <div className="max-w-5xl mx-auto px-4 mt-4 no-print">
          <div className="rounded-md p-5 flex flex-wrap items-center gap-5"
               style={{ background: result.band >= 6.5 ? "rgba(22,163,74,0.08)" : "rgba(247,127,0,0.08)",
                        border: `1.5px solid ${result.band >= 6.5 ? "#16A34A" : "#F77F00"}` }}
               data-testid="listening-result">
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase opacity-60">PREDICTED BAND</div>
              <div className="font-black leading-none" style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 72 }}>
                {result.band.toFixed(1)}
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase opacity-60">RAW SCORE</div>
              <div className="text-2xl font-bold">{result.raw_score} / 40</div>
            </div>
            <div className="text-sm opacity-80 max-w-md">
              Wrong answers are highlighted in red on the sheet below with the correct answer underneath.
            </div>
          </div>
        </div>
      )}

      {/* Paper-style answer sheet */}
      <div className="max-w-3xl mx-auto px-4 mt-6">
        <AnswerSheet
          test={test}
          answers={answers}
          setAnswer={setAnswer}
          candidateName={candidateName}
          today={today}
          locked={stage === "review"}
          result={result}
        />

        {/* Pre-audio fallback CTA at bottom of sheet (mobile-friendly) */}
        {stage === "pre" && (
          <div className="text-center mt-6 no-print">
            <button onClick={startAudio} data-testid="start-audio-secondary"
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-sm font-mono text-sm tracking-[0.25em] uppercase"
                    style={{ background: "#F77F00", color: "#000", boxShadow: "0 0 18px rgba(247,127,0,0.65)" }}>
              <Play className="w-4 h-4" /> Start audio &amp; timer
            </button>
            <div className="text-xs opacity-60 mt-2">Or press Print above to print this sheet first.</div>
          </div>
        )}

        {stage === "playing" && (
          <div className="text-center mt-6 no-print">
            <button onClick={submit} disabled={submitting} data-testid="submit-listening-bottom"
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-sm font-mono text-sm tracking-[0.25em] uppercase"
                    style={{ background: "#003049", color: "#fff" }}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Submit for grading
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────── */
/* Paper-style answer sheet                    */
/* ─────────────────────────────────────────── */

function AnswerSheet({ test, answers, setAnswer, candidateName, today, locked, result }) {
  return (
    <div className="paper-sheet relative mx-auto bg-white rounded-md shadow-2xl overflow-hidden"
         style={{ maxWidth: 780, fontFamily: "'Spectral', Georgia, serif", color: "#222" }}
         data-testid="answer-sheet">
      {/* faint paper texture */}
      <div className="absolute inset-0 pointer-events-none"
           style={{ background: "linear-gradient(180deg, #FFFCF6 0%, #FFFFFF 8%, #FFFFFF 100%)" }} />

      <div className="relative px-10 py-10">
        <div className="text-center font-mono text-[10px] tracking-[0.35em] uppercase opacity-60">
          IELTS LISTENING · ANSWER SHEET
        </div>
        <h2 className="text-center mt-2 mb-3 font-bold tracking-tight"
            style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 28, color: "#003049" }}>
          {test.title}
        </h2>

        <div className="flex flex-wrap items-center justify-center gap-6 text-sm mb-6 opacity-90">
          <div className="flex items-baseline gap-2">
            <span className="opacity-60">Candidate:</span>
            <span className="font-handwriting" style={{ borderBottom: "1px solid #003049", paddingBottom: 2, minWidth: 180, display: "inline-block" }}>
              {candidateName || "\u00A0"}
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="opacity-60">Date:</span>
            <span className="font-handwriting" style={{ borderBottom: "1px solid #003049", paddingBottom: 2, minWidth: 110, display: "inline-block" }}>
              {today}
            </span>
          </div>
        </div>

        <ul className="text-[13px] leading-7 list-disc pl-6 opacity-90 mb-8">
          <li>Write your answers on the lines below — type directly or print the sheet and write by hand.</li>
          <li>For multiple-choice questions, click the letter circle to select it.</li>
          <li>You have <b>30 minutes</b> of audio + 10 minutes (built into the timer) to transfer answers.</li>
          <li><b>Audio plays ONCE only</b> — there is no replay.</li>
          <li>Each correct answer = 1 mark out of 40. Spelling matters.</li>
        </ul>

        {(test.sections || []).map((sec, idx) => (
          <SectionBlock key={sec.section_no}
            section={sec} idx={idx}
            answers={answers} setAnswer={setAnswer}
            qMeta={test.question_meta || []} locked={locked} result={result}
          />
        ))}

        <div className="text-center mt-8 pt-6 border-t" style={{ borderColor: "rgba(0,48,73,0.15)" }}>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase opacity-50">
            // END OF ANSWER SHEET · {test.title}
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionBlock({ section, idx, answers, setAnswer, qMeta, locked, result }) {
  const [start, end] = section.q_range || [idx * 10 + 1, idx * 10 + 10];
  return (
    <div className="mb-8" data-testid={`section-${section.section_no}`}>
      <h3 className="text-lg font-semibold flex items-baseline gap-2" style={{ fontFamily: "'Rajdhani', sans-serif", color: "#003049" }}>
        <span className="opacity-60 text-sm">Part {section.section_no}</span>
        <span>— {section.topic}</span>
      </h3>
      <div className="border-b mt-1 mb-4" style={{ borderColor: "#003049", borderBottomWidth: 1.5 }} />

      <div>
        {Array.from({ length: end - start + 1 }).map((_, k) => {
          const q = start + k;
          const meta = qMeta[q - 1] || { type: "text", options: 0 };
          const item = result?.items?.[q - 1];
          return (
            <QuestionRow key={q}
              q={q} meta={meta}
              value={answers[q - 1]}
              setValue={(v) => setAnswer(q - 1, v)}
              locked={locked}
              item={item}
            />
          );
        })}
      </div>
    </div>
  );
}

function QuestionRow({ q, meta, value, setValue, locked, item }) {
  const correct = item?.is_correct;
  const showFix = locked && !correct && item?.correct_answer;
  return (
    <div className="flex items-center gap-5 py-2.5" data-testid={`q-row-${q}`}>
      <div className="font-mono w-7 text-right text-sm opacity-70">{q}.</div>
      <div className="flex-1">
        {meta.type === "mcq" ? (
          <McqCircles q={q} options={meta.options || 4} value={value} onChange={setValue} locked={locked} />
        ) : (
          <LineInput q={q} value={value} onChange={setValue} locked={locked} correct={correct} />
        )}
        {showFix && (
          <div className="font-handwriting text-[13px] mt-1 ml-1" style={{ color: "#16A34A" }}>
            ✓ {item.correct_answer}
          </div>
        )}
      </div>
      {locked && (
        correct
          ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: "#16A34A" }} />
          : <XCircle className="w-4 h-4 flex-shrink-0" style={{ color: "#D62828" }} />
      )}
    </div>
  );
}

function LineInput({ q, value, onChange, locked, correct }) {
  return (
    <input
      value={value || ""}
      onChange={(e) => onChange(e.target.value.toUpperCase())}
      disabled={locked}
      data-testid={`answer-q${q}`}
      placeholder=""
      autoComplete="off" spellCheck="false"
      className="answer-line w-full bg-transparent outline-none px-1 py-1 font-handwriting"
      style={{
        borderBottom: `1.5px solid ${locked ? (correct ? "#16A34A" : "#D62828") : "#003049"}`,
        fontSize: 18,
        letterSpacing: "0.5px",
        color: locked && !correct ? "#D62828" : "#003049",
      }}
    />
  );
}

function McqCircles({ q, options, value, onChange, locked }) {
  const letters = ["A", "B", "C", "D", "E", "F", "G", "H"].slice(0, options || 4);
  return (
    <div className="flex items-center gap-2.5">
      {letters.map((L) => {
        const active = (value || "").toUpperCase() === L;
        return (
          <button key={L} type="button" disabled={locked}
                  onClick={() => onChange(L)}
                  data-testid={`answer-q${q}-${L}`}
                  className="mcq-circle"
                  style={{
                    width: 30, height: 30, borderRadius: 999,
                    border: `1.5px solid ${active ? "#F77F00" : "#003049"}`,
                    background: active ? "#F77F00" : "transparent",
                    color: active ? "#fff" : "#003049",
                    fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 14,
                    cursor: locked ? "default" : "pointer",
                    transition: "all 0.15s ease",
                  }}>
            {L}
          </button>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────── */
/* Inline CSS                                  */
/* ─────────────────────────────────────────── */
function PlayerCSS() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Patrick+Hand&family=Spectral:wght@400;500;600&display=swap');

      .listening-screen { background: #F4F1EA; min-height: 100vh; }
      .font-handwriting { font-family: 'Patrick Hand', 'Caveat', cursive; }
      .paper-sheet { box-shadow: 0 30px 70px -20px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.04); }
      .answer-line:focus { border-bottom-color: #F77F00 !important; }
      .answer-line:focus::placeholder { color: transparent; }
      .mcq-circle:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 14px rgba(247,127,0,0.25); }

      /* Blinking caret on text lines that have focus */
      .answer-line { caret-color: #F77F00; }

      .animate-pulse-soft { animation: pulseSoft 2.2s ease-in-out infinite; }
      @keyframes pulseSoft {
        0%,100% { box-shadow: 0 0 18px rgba(247,127,0,0.65); }
        50%     { box-shadow: 0 0 24px rgba(247,127,0,0.95); }
      }

      /* Print rules — show only the paper-sheet block, hide the rest */
      @media print {
        body { background: #fff !important; }
        .no-print { display: none !important; }
        .paper-sheet { box-shadow: none !important; max-width: 100% !important; }
        .listening-screen { background: #fff !important; padding: 0 !important; }
        .answer-line, .mcq-circle { color: #000 !important; border-color: #000 !important; background: transparent !important; }
      }
    `}</style>
  );
}
